import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { backdropVariants, scaleIn } from '../../utils/animations';
import LeadDetailModal from './LeadDetailModal';
import BookingDetailDrawer from './BookingDetailDrawer';

interface SearchResult {
  leads: Array<{
    id: string;
    name: string;
    email: string | null;
    status: string;
    source: string;
    notes: string | null;
    websiteId: string;
  }>;
  reviews: Array<{
    id: string;
    reviewerName: string | null;
    reviewText: string | null;
    rating: number;
    platform: string;
    websiteId: string;
  }>;
  websites: Array<{
    id: string;
    domain: string;
    businessName: string | null;
    status: string;
  }>;
  bookings?: Array<{
    id: string;
    customerName: string;
    customerPhone: string;
    customerEmail?: string;
    serviceType: string;
    date: string;
    time: string;
    duration: number;
    status: 'confirmed' | 'pending' | 'completed' | 'cancelled';
    notes?: string;
    createdAt?: string;
    websiteId?: string;
  }>;
}

interface RecentSearch {
  query: string;
  timestamp: number;
}

const RECENT_SEARCHES_KEY = 'foundlio_recent_searches';
const MAX_RECENT_SEARCHES = 5;

// Get icon for each result category
const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'lead':
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
        </svg>
      );
    case 'review':
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
        </svg>
      );
    case 'website':
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 12h17M12 3.5c2 2.5 3.5 5.2 3.5 8.5s-1.5 6-3.5 8.5c-2-2.5-3.5-5.2-3.5-8.5s1.5-6 3.5-8.5z" />
        </svg>
      );
    case 'booking':
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
        </svg>
      );
    default:
      return null;
  }
};

// Get badge for category
const getCategoryBadge = (category: string) => {
  const colors = {
    lead: 'bg-blue-100 text-blue-700',
    review: 'bg-yellow-100 text-yellow-700',
    website: 'bg-green-100 text-green-700',
    booking: 'bg-purple-100 text-purple-700',
  };

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[category as keyof typeof colors] || 'bg-zinc-100 text-zinc-600'}`}>
      {category.charAt(0).toUpperCase() + category.slice(1)}
    </span>
  );
};

// Get status color for leads
const getLeadStatusColor = (status: string) => {
  switch (status) {
    case 'new': return 'text-blue-600';
    case 'contacted': return 'text-yellow-600';
    case 'qualified': return 'text-purple-600';
    case 'converted': return 'text-green-600';
    case 'lost': return 'text-red-600';
    default: return 'text-zinc-600';
  }
};

// Render star rating
const renderStars = (rating: number) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-3 h-3 ${star <= rating ? 'text-yellow-400' : 'text-zinc-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isBookingDrawerOpen, setIsBookingDrawerOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch {
        setRecentSearches([]);
      }
    }
  }, []);

  // Save recent search
  const saveRecentSearch = useCallback((searchQuery: string) => {
    const newSearch: RecentSearch = {
      query: searchQuery,
      timestamp: Date.now(),
    };

    const updated = [
      newSearch,
      ...recentSearches.filter((s) => s.query !== searchQuery),
    ].slice(0, MAX_RECENT_SEARCHES);

    setRecentSearches(updated);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  }, [recentSearches]);

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    // Reset state when opening
    if (isOpen) {
      setQuery('');
      setDebouncedQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Search API query
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['search', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) return null;
      const response = await axios.get(`/api/search?q=${encodeURIComponent(debouncedQuery)}`);
      return response.data.data as SearchResult;
    },
    enabled: debouncedQuery.length >= 2,
  });

  // Flatten results for keyboard navigation
  const flattenedResults = React.useMemo(() => {
    if (!searchResults) return [];

    const results: Array<{ type: string; data: Record<string, unknown>; path: string }> = [];

    searchResults.leads.forEach((lead) => {
      results.push({
        type: 'lead',
        data: lead as unknown as Record<string, unknown>,
        path: `/dashboard/leads?highlight=${lead.id}`,
      });
    });

    searchResults.reviews.forEach((review) => {
      results.push({
        type: 'review',
        data: review as unknown as Record<string, unknown>,
        path: `/dashboard/reviews?highlight=${review.id}`,
      });
    });

    searchResults.websites.forEach((website) => {
      results.push({
        type: 'website',
        data: website as unknown as Record<string, unknown>,
        path: `/dashboard/websites/${website.id}`,
      });
    });

    if (searchResults.bookings) {
      searchResults.bookings.forEach((booking) => {
        results.push({
          type: 'booking',
          data: booking as unknown as Record<string, unknown>,
          path: `/dashboard/bookings?highlight=${booking.id}`,
        });
      });
    }

    return results;
  }, [searchResults]);

  // Handle result click - defined before useEffect that uses it
  const handleResultClick = useCallback((result: { type: string; data: Record<string, unknown>; path: string }) => {
    if (query.length >= 2) {
      saveRecentSearch(query);
    }

    // For leads, open detail modal instead of navigating
    if (result.type === 'lead') {
      setSelectedLead(result.data);
      setIsLeadModalOpen(true);
      onClose();
    } else if (result.type === 'booking') {
      // For bookings, open detail drawer instead of navigating
      setSelectedBooking(result.data);
      setIsBookingDrawerOpen(true);
      onClose();
    } else {
      navigate(result.path);
      onClose();
    }
  }, [query, saveRecentSearch, navigate, onClose]);

  // Reset selectedIndex when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedQuery]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard navigation when we have results
      if (flattenedResults.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => {
            const maxIndex = flattenedResults.length - 1;
            return prev < maxIndex ? prev + 1 : 0;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => {
            const maxIndex = flattenedResults.length - 1;
            return prev > 0 ? prev - 1 : maxIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          setSelectedIndex((currentIndex) => {
            const selectedResult = flattenedResults[currentIndex];
            if (selectedResult) {
              handleResultClick(selectedResult);
            }
            return currentIndex;
          });
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, flattenedResults, onClose, handleResultClick]);

  // Handle recent search click
  const handleRecentSearchClick = (searchQuery: string) => {
    setQuery(searchQuery);
    setDebouncedQuery(searchQuery);
  };

  const hasResults = searchResults && (
    searchResults.leads.length > 0 ||
    searchResults.reviews.length > 0 ||
    searchResults.websites.length > 0 ||
    (searchResults.bookings && searchResults.bookings.length > 0)
  );

  const showRecentSearches = !query && recentSearches.length > 0;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              variants={backdropVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={onClose}
            />

            {/* Modal - centered in entire browser viewport */}
            <motion.div
              variants={scaleIn}
              initial="initial"
              animate="animate"
              exit="exit"
              className="fixed top-[35%] left-1/2 w-[calc(100%-2rem)] max-w-2xl bg-white rounded-xl shadow-2xl border border-zinc-200 overflow-hidden z-50"
              style={{ x: '-50%' }}
            >
            {/* Search Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-200">
              <svg className="w-5 h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search leads, reviews, websites, bookings..."
                className="flex-1 bg-transparent text-primary placeholder-zinc-400 outline-none focus:outline-none focus:ring-0 border-0 text-base"
                style={{ boxShadow: 'none' }}
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="p-1 hover:bg-zinc-100 rounded transition-colors"
                >
                  <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <div className="flex items-center gap-1 text-xs text-muted">
                <kbd className="px-1.5 py-0.5 bg-zinc-100 rounded text-xs">esc</kbd>
                <span>to close</span>
              </div>
            </div>

            {/* Results Area */}
            <div className="max-h-96 overflow-y-auto">
              {/* Loading State */}
              {isLoading && (
                <div className="p-8 text-center">
                  <div className="w-6 h-6 border-2 border-zinc-200 border-t-accent rounded-full animate-spin mx-auto"></div>
                  <p className="text-sm text-muted mt-2">Searching...</p>
                </div>
              )}

              {/* Recent Searches */}
              {showRecentSearches && (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted uppercase tracking-wider">Recent Searches</span>
                    <button
                      onClick={clearRecentSearches}
                      className="text-xs text-zinc-400 hover:text-zinc-600"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="space-y-1">
                    {recentSearches.map((search) => (
                      <button
                        key={search.timestamp}
                        onClick={() => handleRecentSearchClick(search.query)}
                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-100 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                        <span className="text-sm text-primary">{search.query}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {!isLoading && debouncedQuery.length >= 2 && !hasResults && (
                <div className="p-8 text-center">
                  <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                    </svg>
                  </div>
                  <p className="text-sm text-muted">No results found for "{debouncedQuery}"</p>
                  <p className="text-xs text-zinc-400 mt-1">Try a different search term</p>
                </div>
              )}

              {/* Search Results */}
              {!isLoading && hasResults && (
                <div className="p-2">
                  {/* Leads */}
                  {searchResults.leads.length > 0 && (
                    <div className="mb-2">
                      <div className="px-3 py-1.5 text-xs font-medium text-muted uppercase tracking-wider">
                        Leads ({searchResults.leads.length})
                      </div>
                      {searchResults.leads.map((lead, idx) => {
                        const resultIndex = idx;
                        const isSelected = selectedIndex === resultIndex;
                        return (
                          <button
                            key={lead.id}
                            onClick={() => handleResultClick({ type: 'lead', data: lead as unknown as Record<string, unknown>, path: `/dashboard/leads?highlight=${lead.id}` })}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isSelected ? 'bg-accent/20 ring-2 ring-accent' : 'hover:bg-zinc-100'
                            }`}
                          >
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                              {getCategoryIcon('lead')}
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-primary truncate">{lead.name}</span>
                                <span className={`text-xs ${getLeadStatusColor(lead.status)}`}>
                                  {lead.status}
                                </span>
                              </div>
                              <p className="text-xs text-muted truncate">
                                {lead.email || lead.source}
                              </p>
                            </div>
                            {getCategoryBadge('lead')}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Reviews */}
                  {searchResults.reviews.length > 0 && (
                    <div className="mb-2">
                      <div className="px-3 py-1.5 text-xs font-medium text-muted uppercase tracking-wider">
                        Reviews ({searchResults.reviews.length})
                      </div>
                      {searchResults.reviews.map((review, idx) => {
                        const resultIndex = (searchResults.leads?.length || 0) + idx;
                        const isSelected = selectedIndex === resultIndex;
                        return (
                          <button
                            key={review.id}
                            onClick={() => handleResultClick({ type: 'review', data: review as unknown as Record<string, unknown>, path: `/dashboard/reviews?highlight=${review.id}` })}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isSelected ? 'bg-accent/20 ring-2 ring-accent' : 'hover:bg-zinc-100'
                            }`}
                          >
                            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center text-yellow-600">
                              {getCategoryIcon('review')}
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-primary truncate">
                                  {review.reviewerName || 'Anonymous'}
                                </span>
                                {renderStars(review.rating)}
                              </div>
                              <p className="text-xs text-muted truncate">
                                {review.reviewText || `${review.platform} review`}
                              </p>
                            </div>
                            {getCategoryBadge('review')}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Websites */}
                  {searchResults.websites.length > 0 && (
                    <div className="mb-2">
                      <div className="px-3 py-1.5 text-xs font-medium text-muted uppercase tracking-wider">
                        Websites ({searchResults.websites.length})
                      </div>
                      {searchResults.websites.map((website, idx) => {
                        const resultIndex = (searchResults.leads?.length || 0) + (searchResults.reviews?.length || 0) + idx;
                        const isSelected = selectedIndex === resultIndex;
                        return (
                          <button
                            key={website.id}
                            onClick={() => handleResultClick({ type: 'website', data: website as unknown as Record<string, unknown>, path: `/dashboard/websites/${website.id}` })}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isSelected ? 'bg-accent/20 ring-2 ring-accent' : 'hover:bg-zinc-100'
                            }`}
                          >
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                              {getCategoryIcon('website')}
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-primary truncate">
                                  {website.businessName || website.domain}
                                </span>
                              </div>
                              <p className="text-xs text-muted truncate">{website.domain}</p>
                            </div>
                            {getCategoryBadge('website')}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Bookings */}
                  {searchResults.bookings && searchResults.bookings.length > 0 && (
                    <div>
                      <div className="px-3 py-1.5 text-xs font-medium text-muted uppercase tracking-wider">
                        Bookings ({searchResults.bookings.length})
                      </div>
                      {searchResults.bookings.map((booking, idx) => {
                        const resultIndex = (searchResults.leads?.length || 0) + (searchResults.reviews?.length || 0) + (searchResults.websites?.length || 0) + idx;
                        const isSelected = selectedIndex === resultIndex;
                        return (
                          <button
                            key={booking.id}
                            onClick={() => handleResultClick({ type: 'booking', data: booking as unknown as Record<string, unknown>, path: `/dashboard/bookings?highlight=${booking.id}` })}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isSelected ? 'bg-accent/20 ring-2 ring-accent' : 'hover:bg-zinc-100'
                            }`}
                          >
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">
                              {getCategoryIcon('booking')}
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-primary truncate">
                                  {booking.customerName}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                  booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                  booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {booking.status}
                                </span>
                              </div>
                              <p className="text-xs text-muted truncate">
                                {booking.serviceType} • {new Date(booking.date).toLocaleDateString()}
                              </p>
                            </div>
                            {getCategoryBadge('booking')}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Initial State */}
              {!isLoading && !showRecentSearches && debouncedQuery.length < 2 && (
                <div className="p-8 text-center">
                  <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                    </svg>
                  </div>
                  <p className="text-sm text-muted">Start typing to search</p>
                  <p className="text-xs text-zinc-400 mt-1">Search leads, reviews, websites, and bookings</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-zinc-200 bg-zinc-50">
              <div className="flex items-center justify-between text-xs text-muted">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border border-zinc-200 rounded shadow-sm">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                      </svg>
                    </kbd>
                    <kbd className="px-1.5 py-0.5 bg-white border border-zinc-200 rounded shadow-sm">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </kbd>
                    to navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border border-zinc-200 rounded shadow-sm text-[10px]">
                      enter
                    </kbd>
                    to select
                  </span>
                </div>
                <span>Powered by Foundlio</span>
              </div>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Lead Detail Modal - separate AnimatePresence so it doesn't close with search */}
      <LeadDetailModal
        lead={selectedLead}
        isOpen={isLeadModalOpen}
        onClose={() => {
          setIsLeadModalOpen(false);
          setSelectedLead(null);
        }}
      />

      {/* Booking Detail Drawer - separate AnimatePresence so it doesn't close with search */}
      <BookingDetailDrawer
        booking={selectedBooking}
        isOpen={isBookingDrawerOpen}
        onClose={() => {
          setIsBookingDrawerOpen(false);
          setSelectedBooking(null);
        }}
      />
    </>
  );
};

export default SearchModal;
